import { useVisitStore } from "@/store/visits/visitStore";
import { useVisitTokenStore } from "@/store/visits/visitTokenStore";
import { useEffect, useMemo, useState } from "react";
import type { TimeSlotWithSearchProps } from "@/components/shared/time-slot-with-search";
import { getServiceNameFromDepartment } from "@/lib/booking-lib";
import {
  departmentPackageToWorker,
  getWorkerLabel,
} from "@/lib/service/helpers";
import { getCapitalizeWord } from "@/lib/utils";
import { useVisitDetailStore } from "@/store/visits/visitDetail";
import type { ClientFindBookingScheduleQueryVariables } from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  ScheduleJobFrequencyEnum,
} from "@/__generated__/graphql";
import { useClientFindBookingScheduleQuery } from "@/gqlhooks/booking/useClientFindBookingScheduleQuery";
import { addDays } from "date-fns";
import type { Worker, TimeSlot } from "@/types/booking";
import { useClientRescheduleVisit } from "@/store/visits/useClientRescheduleVisit";
import { useClientRescheduleWithToken } from "@/store/visits/useClientRescheduleWithToken";
import { getErrorMessage } from "@/lib/helpers/string";
import { showToast } from "@/components/ui/toast/show-toast";
import { useRoute } from "@/components/shared/router";
import { formatDate, today } from "@/lib/helpers/date";
import { collectError } from "@/lib/monitoring/sentry";
import { RescheduleVisitContentScreen } from "./reschedule-visit-content-screen";

export type RescheduleVisitContentProps = {
  timeSlotProps: TimeSlotWithSearchProps;
  onConfirm: () => Promise<void>;
  disabledButton?: boolean | null | undefined;
  infoDialogSuccess: boolean;
  onCloseInfoDialog: () => void;
  chooseWorkerDepartment: PackageDepartmentEnum;
  openChooseWorker: boolean;
  setOpenChooseWorker(open: boolean): void;
  selectedWorkers: Worker[];
  handleApply(workers: Worker[]): void;
  openSuitableWorker: boolean;
  onCloseOpenSuitableWorker(): void;
  visitWorker: Worker | null;
};

const timeRangeFindSchedule = [
  { startTime: "06:00", endTime: "22:00", day: 0 },
  { startTime: "06:00", endTime: "22:00", day: 1 },
  { startTime: "06:00", endTime: "22:00", day: 2 },
  { startTime: "06:00", endTime: "22:00", day: 3 },
  { startTime: "06:00", endTime: "22:00", day: 4 },
  { startTime: "06:00", endTime: "22:00", day: 5 },
  { startTime: "06:00", endTime: "22:00", day: 6 },
];

export function RescheduleVisitContent({
  visitId,
  token,
}: {
  visitId?: string;
  token?: string;
}) {
  const {
    fetch: getVisit,
    data: visitData,
    loading: loadingVisit,
  } = useVisitStore();
  const {
    fetch: getVisitToken,
    data: visitToken,
    loading: loadingVisitToken,
  } = useVisitTokenStore();

  const { fetch: rescheduleVisit, loading: rescheduleLoading } =
    useClientRescheduleVisit();
  const { fetch: rescheduleToken, loading: rescheduleTokenLoading } =
    useClientRescheduleWithToken();

  const loadingMutation = rescheduleLoading || rescheduleTokenLoading;

  const {
    transformedData,
    execute: getSlots,
    loading: scheduleLoading,
  } = useClientFindBookingScheduleQuery();

  const workerType = useVisitDetailStore(
    (state) => state.data.rescheduleVisitWorkerType,
  );
  const setVisitDepartment = useVisitDetailStore(
    (state) => state.setVisitDepartment,
  );

  const visit = token ? visitToken : visitData;
  const loading = loadingVisit || loadingVisitToken;

  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(addDays(today(), 6));
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [chooseWorkerModalOpen, setChooseWorkerModalOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showSuitableWorkerModal, setShowSuitableWorkerModal] = useState(false);

  const [currentWorkers, setCurrentWorkers] = useState<Worker[]>(
    workerType === "CURRENT_WORKER" && visit?.worker ? [visit.worker] : [],
  );

  const scheduleFilter =
    useMemo((): ClientFindBookingScheduleQueryVariables | null => {
      if (!visit) return null;

      const visitLineItemIds = visit.lineItems.map(
        (lineItem) => lineItem.packageDetailId,
      );
      const visitWorkerSkillIds = visit.lineItems.map(
        (lineItem) => lineItem.workerSkillId,
      );

      let workersIds: string[] | null = null;
      if (currentWorkers.length) {
        workersIds = currentWorkers.map((worker) => worker.id);
      } else if (workerType === "ANY_WORKER") {
        workersIds = null;
      }

      return {
        department: departmentPackageToWorker(visit.department),
        frequencyOfJob: ScheduleJobFrequencyEnum.AdHoc,
        sessionDuration: 0,
        postalCode: visit.postalCode,
        timeRanges: timeRangeFindSchedule,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        lineItemIds: visitLineItemIds,
        workerSkillIds: visitWorkerSkillIds,
        workersIds,
        hasPortfolios:
          visit.department === PackageDepartmentEnum.HomeBeauty ||
          visit.department === PackageDepartmentEnum.ElderCare,
      };
    }, [visit, startDate, endDate, workerType, currentWorkers]);

  const { push } = useRoute();

  useEffect(() => {
    if (token) {
      getVisitToken({ requestPayload: { token } });
    } else if (visitId) {
      getVisit({ requestPayload: { id: visitId } });
    }
  }, [visitId, token]);

  useEffect(() => {
    if (workerType === "ANY_WORKER" && currentWorkers.length) {
      setCurrentWorkers([]);
    }
  }, [workerType]);

  useEffect(() => {
    if (visit) {
      setVisitDepartment(visit.department);
    }
    if (scheduleFilter) {
      getSlots({ variables: scheduleFilter });
    }
  }, [scheduleFilter, visit, setVisitDepartment, getSlots]);

  const name = useMemo(
    () =>
      getServiceNameFromDepartment(
        visit?.department ?? PackageDepartmentEnum.HomeCleaning,
      ),
    [visit?.department],
  );

  const workerLabel = useMemo(
    () =>
      getCapitalizeWord(
        getWorkerLabel(visit?.department ?? PackageDepartmentEnum.HomeCleaning),
      ),
    [visit?.department],
  );

  const onConfirm = async () => {
    if (!visit) {
      showToast({ title: "Invalid Visit data", type: "error" });
      return;
    }

    if (!selectedSlot) {
      showToast({
        title: "Please select a time slot first",
        type: "error",
      });
      return;
    }

    const input = {
      serviceDate: selectedSlot.date,
      startTime: selectedSlot.startTime,
      endTime: selectedSlot.endTime,
      workerId: selectedSlot.workerId,
    };

    try {
      const result = token
        ? await rescheduleToken(
            { requestPayload: { input: { ...input, token } } },
            { selfHandleError: true },
          )
        : await rescheduleVisit(
            { requestPayload: { input: { ...input, id: String(visitId) } } },
            { selfHandleError: true },
          );

      if (result.error) {
        throw new Error(result.error);
      }

      if (result.data) {
        setSuccess(true);
      }
    } catch (error) {
      console.log("Unhandled submit request reschedule", error);
      let errorMsg = getErrorMessage(error, "Failed to request reschedule");

      if (errorMsg.includes("Overlap visit found with id:")) {
        errorMsg =
          "The slot has already been taken by another customer, please try another slot.";
      }

      showToast({ type: "error", title: errorMsg });
      if (scheduleFilter) {
        getSlots({ variables: scheduleFilter });
      }
      collectError(error, {
        extra: { rescheduleVisit: { selectedSlot, input } },
      });
    }
  };

  const handleSuccessReschedule = () => {
    setSuccess(false);
    setSelectedSlot(null);
    push({ pageKey: "visits" });
  };

  const onChooseWorkers = () => {
    setChooseWorkerModalOpen(true);
    setShowSuitableWorkerModal(false);
  };

  const _setCurrentWorkers = (workers: Worker[]) => {
    setCurrentWorkers(workers);
    if (workers.length) {
      // add delay to open other modal to ensure previous modal closed
      setTimeout(() => {
        setShowSuitableWorkerModal(true);
      }, 1000);
    }
  };

  const timeSlotProps: TimeSlotWithSearchProps = {
    name,
    initialStartDate: today(),
    department: visit?.department ?? PackageDepartmentEnum.HomeCleaning,
    showTimeRange: true,
    loading: loading || scheduleLoading,
    selectedSlot,
    timeSlots: transformedData,
    workersIds: currentWorkers?.map((worker) => worker.id) || [],
    workerLabel,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    enableChooseWorker: true,
    onChooseWorkers,
    onSelectSlot: setSelectedSlot,
    onClickWorkerPortfolio: () => {},
    hidePricing: visit?.department === PackageDepartmentEnum.HomeCleaning,
  };

  return (
    <RescheduleVisitContentScreen
      timeSlotProps={timeSlotProps}
      onConfirm={onConfirm}
      disabledButton={loadingMutation}
      infoDialogSuccess={success}
      onCloseInfoDialog={handleSuccessReschedule}
      chooseWorkerDepartment={
        visit?.department ?? PackageDepartmentEnum.HomeCleaning
      }
      openChooseWorker={chooseWorkerModalOpen}
      setOpenChooseWorker={setChooseWorkerModalOpen}
      selectedWorkers={currentWorkers ?? []}
      handleApply={_setCurrentWorkers}
      visitWorker={visit?.worker ?? null}
      openSuitableWorker={showSuitableWorkerModal}
      onCloseOpenSuitableWorker={() => setShowSuitableWorkerModal(false)}
    />
  );
}
